import React from "react";
import ReactDOM from "react-dom";
import BaseComponent from "./BaseComponent";
import InputSuggestion from "./InputSuggestion";
import _ from "lodash";
import { variableScope } from "../utilities/functions/validations/variable-scope";
import { Label } from "./label";
import { variableAndDigitSearch } from "../const";

// Autocomplete text like `{{app.foo` that is going to reference a variable.
const partialVariableSearch = new RegExp(`\\{*(${variableScope})(\\.[\\w-]*)(?!\\}{2}$)\\}*$`);

export default class Textarea extends BaseComponent {
    _hasChanged = false;

    /**
     * Multi-line text area
     * @module Textarea
     * @param {Object} props React props
     */
    constructor(props){
        super(props);
        this.state = {
            value: this.props.value ? this.props.value : ""
        };
        this.initSupportedRules();
        this.suggestionInstance = null;
        this.textareaRef = React.createRef();

        this.clickOutsideHandler = this.handleClickOutside.bind(this);
        this.getValue = this.getValue.bind(this);
        this.handleHighlight = this.handleHighlight.bind(this);
        this.handleOnChange = this.handleOnChange.bind(this);
        this.onChangeHandler = this.handleOnChange.bind(this);
        this.updateValue = this.updateValue.bind(this);
        this.validate = _.debounce(this.validate.bind(this), 500);
    }

    UNSAFE_componentWillReceiveProps(nextProps){
        if(nextProps.value !== this.state.value){
            this.setState({value: nextProps.value});
        }
    }

    /**
     * Get reference to textarea input box itself
     * @returns {jQuery}
     */
    getDomSelf(){
        return this.textareaRef?.current ? $(this.textareaRef?.current) : $(null);
    }

    componentDidMount(){        
        this.validate();
        this.handleHighlight();
        document.addEventListener('mousedown', this.clickOutsideHandler, true);
        document.addEventListener('wheel', this.clickOutsideHandler, { passive: false });
    }

    componentWillUnmount() {
        this.updateValue();
        document.removeEventListener('mousedown', this.clickOutsideHandler, true);
        document.removeEventListener('wheel', this.clickOutsideHandler);
        this.validate();
    }

    setWrapperRef(node) {
        this.wrapperRef = node;
    }

    handleClickOutside(event) {
        if (this.wrapperRef && !this.wrapperRef.contains(event.target)) {
            this.hideSuggestion();
        }
    }

    /**
     * Get the snippet for use with suggestion
     * @returns {null|RegExpMatchArray}
     */
    getSuggestionSnippet(){
        const cPos = this.getDomSelf().getCursorPosition();
        if (cPos > 0) {
            const clip = this.getDomSelf().val().substring(0, cPos);
            return clip.match(partialVariableSearch);
        }
        return null;
    }

    /**
     * Show suggestion box
     * @param {null|Array} snippet Snippet of user input that triggers the suggestion
     * @param {Object} suggestions List of suggestion items
     */
    showSuggestion(snippet, suggestions){
        this.hideSuggestion();
        const $suggestion = this.getDomSelf().parent().parent().find(".suggestion");
        var hasSuggestions = false;
        for (var key in suggestions){
            if (suggestions.hasOwnProperty(key) && suggestions[key].length){
                hasSuggestions = true;
                break;
            }
        }
        if ($suggestion.length && hasSuggestions){
            $suggestion.show();
            this.suggestionInstance = ReactDOM.render(
                <InputSuggestion
                    context={this}
                    snippet={snippet}
                    suggestions={suggestions}
                    type="variable"
                />,
                $suggestion.get(0)
            );
            this.isSuggestionShown = true;
        }
    }

    /**
     * Hide suggestion box
     */
    hideSuggestion(){
        if (this.suggestionInstance) {
            this.suggestionInstance.unregister();
        }
        const s = this.getDomSelf().parent().parent().find(".suggestion");
        if (s){
            s.hide();
            this.isSuggestionShown = false;
        }
    }
    initSupportedRules(){
        this.supportedValRules = {
            "noEmpty": {
                "f": function(value){
                    if (!value){
                        return false;
                    }
                    const trimmed = value.trim();
                    return trimmed !== "";
                },
                "t": this.getPropLabel("no_empty")
            }
        }
    }
    
    getErrorContainer(){
        return $(ReactDOM.findDOMNode(this)).children(".form-error-tip");
    }
    showErrorTip(tip, type = "error"){
        this.getErrorContainer().addClass(type).html(tip).show();
        if (type === "warning"){
            // Auto hide warning after timeout
            if (this.timer !== null){
                clearTimeout(this.timer);
                this.timer = null;
            }
            this.timer = setTimeout(this.hideErrorTip.bind(this), 3000);
        }
    }
    hideErrorTip() {
        this.getDomSelf().removeClass("error").removeClass("warning");
        this.getErrorContainer().removeClass("error").removeClass("warning").html("&nbsp;").hide();
    }

    validate(){
        if (!this.props.valRules){
            return true;
        }

        const value = this.getValue();

        if (value !== undefined) {
            for (var key in this.props.valRules){

                const ruleValue = this.props.valRules[key];

                let isValid, warningMessage; 

                if (typeof ruleValue === "function") {
                    warningMessage = ruleValue(value)?.join('\n');
                    isValid = !(warningMessage);
                } else if (this.supportedValRules[key]) {
                    warningMessage = this.supportedValRules[key].t;
                    isValid = this.supportedValRules[key].f(value, ruleValue, event);
                } else {
                    continue;
                }
                
                if (!isValid){
                    this.showErrorTip(warningMessage);
                    this.props?.context?.setError?.("inputValRules::" + this.props.valKey + "::" + key, warningMessage);
                    return false;
                } else {
                    this.hideErrorTip();
                    this.props?.context?.removeError?.("inputValRules::" + this.props.valKey + "::" + key);
                }
            }
        }
        return true;
    }

    handleHighlight(){
        if (this.props.highlight){
            // Highlighting {{}} variable embedding syntax
            this.getDomSelf().highlightWithinTextarea({
                highlight: variableAndDigitSearch
            }).highlightWithinTextarea('update');
        }
    }

    handleSuggestions(){
        if (this.props.highlight){
            this.hideSuggestion();
            const snippet = this.getSuggestionSnippet();
            if (snippet) {
                const suggestion = window.apm.knowledgeBase.getVariableSuggestions(snippet[2].slice(1), snippet[1]);
                this.showSuggestion(snippet, suggestion);
            }
        }
    }

    getValue(){
        const value = this.getDomSelf().val();
        return value === '0' ? '' : value;
    }

    /**
     * Apply user-selected suggestion item back to embed area in textarea
     * @param {string} suggestion The user-selected suggestion value
     */
    applySuggestion(suggestion){
        this.hideSuggestion();
        const snippet = this.getSuggestionSnippet();
        if (snippet) {
            const length = snippet[0].length;
            const end = this.getDomSelf().getCursorPosition();
            const start = end - length;
            const currentVal = this.getDomSelf().val();
            const startText = currentVal.substring(0, start).replace(/\{*$/, '');
            const endText = currentVal.substring(end).replace(/^\}*/, '');

            // Replace entirely the snippet text with suggestion value
            this.getDomSelf().val(`${startText}{{${suggestion}}}${endText}`);
            this.handleOnChange();
            if (this.props?.context) {
                this.updateContext(this.getValue());
            }
            this.getDomSelf().highlightWithinTextarea('update');
            this.validate();
            this.getDomSelf().trigger("focus");
        }
    }

    handleOnChange() {
        let value = this.getValue();
        this._hasChanged = true;
        this.setState({ value });
        if (value !== (this.props.value ?? "")) {
            if (this.props?.onChange) {
                this.props.onChange(value);
            }
        }
    }

    updateValue() {
        if (!this._hasChanged) {
            return;
        }
        const propsValue = this.props.value ?? '';
        const inputValue = this.getValue();
        if (inputValue !== propsValue) {
            this.setState({ value: inputValue });
            if(this.props?.context) {
                this.updateContext(inputValue, { lang: this.props.lang });
            }
        }
        this._hasChanged = false;
    }

    /**
     * React render
     * @returns {XML}
     */
    render(){
        const placeholder = this.props.placeholder ?? "";
        const rows = this.props.rows ?? 5;
        const spellCheck = this.props.spellCheck ?? true;
        const style = this.props.style || {};
        let className = this.props.className || "";
        className += " form-component-container";
        return (
            <div className={className}
                 ref={this.setWrapperRef.bind(this)}
            >
                <Label tooltip={this.props.tooltip}>{this.props.label}</Label>

                <div className="form-component textarea" style={{position: "relative"}}>
                    <textarea
                        ref={this.textareaRef}
                        disabled={this.props.readOnly}
                        onBlur={this.updateValue}
                        onChange={this.handleOnChange}
                        onInput={() => {
                            this.handleSuggestions();
                        }}
                        onKeyDown={(event) => {
                            this.hideErrorTip();
                            this.validate();
                            switch (event.keyCode) {
                                case 13: // enter
                                    if (this.isSuggestionShown){
                                        event.preventDefault();
                                        event.stopPropagation();
                                        const canvasEv = new CustomEvent('ev-input-nav-key', { detail: "enter" });
                                        document.dispatchEvent(canvasEv);
                                    }
                                    break;
                                case 27: // esc
                                    this.hideSuggestion();
                                    break;
                                case 38: // up
                                    if (this.isSuggestionShown){
                                        event.preventDefault();
                                        event.stopPropagation();
                                        const canvasEv = new CustomEvent('ev-input-nav-key', { detail: "up" });
                                        document.dispatchEvent(canvasEv);
                                    }
                                    break;
                                case 40: // down
                                    if (this.isSuggestionShown){
                                        event.preventDefault();
                                        event.stopPropagation();
                                        const canvasEv = new CustomEvent('ev-input-nav-key', { detail: "down" });
                                        document.dispatchEvent(canvasEv);
                                    }
                                    break;
                            }
                        }}
                        placeholder={placeholder}
                        rows={rows}
                        spellCheck={spellCheck}
                        style={style}
                        value={this.state.value}
                    />
                    { this.props.highlight && <div className="suggestion form-list" /> }
                </div>
                <div className="form-error-tip">&nbsp;</div>
            </div>
        );
    }
}
